import {
  createSlice,
  createAsyncThunk,
  type PayloadAction,
} from "@reduxjs/toolkit";
import type {
  DashboardState,
  DashboardApiResponse,
  ViewsTriggersApiResponse,
  TriggerData,
  StatusInfo,
  ContactsApiResponse,
  AnonymousQuestion,
  MonthlySourcesData,
  Ratings,
} from "../types/dashboard";
import {
  fetchDashboardData,
  fetchDashboardDataWithPeriod,
  fetchViewsTriggersData,
  fetchYearlyViewsTriggersData,
  fetchWeeklyViewsTriggersData,
  fetchDailyViewsTriggersData,
  fetchForeverViewsTriggersData,
  fetchTriggersData,
  fetchContactsData,
  createShortLink,
  fetchStatusMe,
  fetchViewsSourcesData,
  fetchRatingsData,
  fetchUserBoxes,
  deleteBox,
} from "../utils/apiService";

const SELECTED_STATUS_KEY = "twistatus_selected_status_id";

const getPersistedStatusId = (): number | null => {
  const stored = sessionStorage.getItem(SELECTED_STATUS_KEY);
  if (stored) {
    const parsed = parseInt(stored, 10);
    return isNaN(parsed) ? null : parsed;
  }
  return null;
};

const initialState: DashboardState = {
  status: null,
  statusIdFromUrl: getPersistedStatusId(),
  loadingAll: false,
  overviewLoading: false,
  monthlyLoading: false,
  weeklyLoading: false,
  dailyLoading: false,
  triggersLoading: false,
  contactsLoading: false,
  ratingsLoading: false,
  anonymousQuestionsLoading: false,
  errorAll: null,
  data: {
    overview: {
      all: null,
      month: null,
      year: null,
      week: null,
      day: null,
    },
    by_source: {
      all: null,
      month: null,
      week: null,
      day: null,
    },
    by_triggers: {
      all: [],
      month: [],
      year: [],
      week: [],
      day: [],
      forever: [],
    },
    views: {
      month: [],
      year: [],
      week: [],
      day: [],
    },
    ratings: {
      all: null,
      month: null,
      week: null,
      day: null,
      filtered: null,
    },
    triggers: [],
    contacts: [],
    contactsPagination: null,
    monthlySourcesData: null,
    anonymousQuestions: [],
  },
};

export const fetchAnonymousQuestionsAction = createAsyncThunk(
  "dashboard/fetchAnonymousQuestions",
  async (ownerId: string | number, { rejectWithValue }) => {
    try {
      const boxes = await fetchUserBoxes(ownerId);
      return Array.isArray(boxes) ? boxes : [];
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to fetch anonymous questions"
      );
    }
  }
);

export const deleteAnonymousQuestionAction = createAsyncThunk(
  "dashboard/deleteAnonymousQuestion",
  async (boxId: string, { rejectWithValue }) => {
    try {
      await deleteBox(boxId);
      return boxId;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to delete anonymous question"
      );
    }
  }
);

export const fetchDashboardDataAction = createAsyncThunk(
  "dashboard/fetchData",
  async (_, { rejectWithValue }) => {
    try {
      const response: DashboardApiResponse = await fetchDashboardData();

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to fetch dashboard data"
      );
    }
  }
);

export const fetchDashboardDataWithPeriodAction = createAsyncThunk(
  "dashboard/fetchDashboardDataWithPeriod",
  async (period: string, { rejectWithValue }) => {
    try {
      const response: DashboardApiResponse = await fetchDashboardDataWithPeriod(
        period
      );

      if (response.success && response.data) {
        return { period, data: response.data };
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to fetch dashboard data with period"
      );
    }
  }
);

export const fetchViewsTriggersDataAction = createAsyncThunk(
  "dashboard/fetchViewsTriggersData",
  async (_, { rejectWithValue }) => {
    try {
      const response: ViewsTriggersApiResponse = await fetchViewsTriggersData();

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to fetch views/triggers data"
      );
    }
  }
);

export const fetchYearlyViewsTriggersDataAction = createAsyncThunk(
  "dashboard/fetchYearlyViewsTriggersData",
  async (_, { rejectWithValue }) => {
    try {
      const response: ViewsTriggersApiResponse =
        await fetchYearlyViewsTriggersData();

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to fetch yearly views/triggers data"
      );
    }
  }
);

export const fetchWeeklyViewsTriggersDataAction = createAsyncThunk(
  "dashboard/fetchWeeklyViewsTriggersData",
  async (_, { rejectWithValue }) => {
    try {
      const response: ViewsTriggersApiResponse =
        await fetchWeeklyViewsTriggersData();

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to fetch weekly views/triggers data"
      );
    }
  }
);

export const fetchDailyViewsTriggersDataAction = createAsyncThunk(
  "dashboard/fetchDailyViewsTriggersData",
  async (_, { rejectWithValue }) => {
    try {
      const response: ViewsTriggersApiResponse =
        await fetchDailyViewsTriggersData();

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to fetch daily views/triggers data"
      );
    }
  }
);

export const fetchForeverViewsTriggersDataAction = createAsyncThunk(
  "dashboard/fetchForeverViewsTriggersData",
  async (_, { rejectWithValue }) => {
    try {
      const response: ViewsTriggersApiResponse =
        await fetchForeverViewsTriggersData();

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to fetch forever views/triggers data"
      );
    }
  }
);

export const fetchTriggersDataAction = createAsyncThunk(
  "dashboard/fetchTriggersData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchTriggersData();
      return response as TriggerData[];
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to fetch triggers data"
      );
    }
  }
);

export const fetchContactsDataAction = createAsyncThunk(
  "dashboard/fetchContactsData",
  async (
    {
      fromDate,
      toDate,
      limit,
      offset,
      append = false,
      source,
      query,
      triggerId,
    }: {
      statusId: number;
      fromDate?: string;
      toDate?: string;
      limit?: number;
      offset?: number;
      append?: boolean;
      source?: string;
      query?: string;
      triggerId?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response: ContactsApiResponse = await fetchContactsData(
        fromDate,
        toDate,
        limit,
        offset,
        source,
        query,
        triggerId
      );

      if (response.success) {
        return {
          contacts: response.data || [],
          pagination: response.pagination,
          append,
        };
      } else {
        throw new Error("Invalid contacts API response structure");
      }
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to fetch contacts data"
      );
    }
  }
);

export const createShortLinkAction = createAsyncThunk(
  "dashboard/createShortLink",
  async (triggerId: number, { rejectWithValue }) => {
    try {
      const response = await createShortLink(triggerId);
      return { triggerId, short_link_id: response.short_link_id };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to create short link"
      );
    }
  }
);

export const fetchStatusMeAction = createAsyncThunk(
  "dashboard/fetchStatusMe",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchStatusMe();
      return response;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to fetch status me data"
      );
    }
  }
);

export const fetchViewsSourcesDataAction = createAsyncThunk(
  "dashboard/fetchViewsSourcesData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchViewsSourcesData();
      if (response && response.data) {
        return response.data as MonthlySourcesData;
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to fetch views sources data"
      );
    }
  }
);

export const fetchRatingsDataAction = createAsyncThunk(
  "dashboard/fetchRatingsData",
  async (
    params: { statusId: number; from?: string; to?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await fetchRatingsData(params.from, params.to);
      if (response && response.success && response.data) {
        return response.data as Ratings;
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to fetch ratings data"
      );
    }
  }
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    setStatusIdFromUrl: (state, action: PayloadAction<number | null>) => {
      state.statusIdFromUrl = action.payload;
      if (action.payload !== null) {
        sessionStorage.setItem(SELECTED_STATUS_KEY, String(action.payload));
      } else {
        sessionStorage.removeItem(SELECTED_STATUS_KEY);
      }
    },
    clearData: (state) => {
      state.status = null;
      state.statusIdFromUrl = null;
      sessionStorage.removeItem(SELECTED_STATUS_KEY);
      state.overviewLoading = false;
      state.monthlyLoading = false;
      state.weeklyLoading = false;
      state.dailyLoading = false;
      state.triggersLoading = false;
      state.contactsLoading = false;
      state.ratingsLoading = false;
      state.anonymousQuestionsLoading = false;
      state.data = {
        overview: {
          all: null,
          month: null,
          year: null,
          week: null,
          day: null,
        },
        by_source: {
          all: null,
          month: null,
          week: null,
          day: null,
        },
        by_triggers: {
          all: [],
          month: [],
          year: [],
          week: [],
          day: [],
          forever: [],
        },
        views: {
          month: [],
          year: [],
          week: [],
          day: [],
        },
        ratings: {
          all: null,
          month: null,
          week: null,
          day: null,
          filtered: null,
        },
        triggers: [],
        contacts: [],
        contactsPagination: null,
        monthlySourcesData: null,
        anonymousQuestions: [],
      };
      state.errorAll = null;
    },
    setAnonymousQuestions: (state, action: PayloadAction<AnonymousQuestion[]>) => {
      state.data.anonymousQuestions = action.payload;
    },
    addAnonymousQuestion: (state, action: PayloadAction<AnonymousQuestion>) => {
      state.data.anonymousQuestions.unshift(action.payload);
    },
    deleteAnonymousQuestion: (state, action: PayloadAction<string>) => {
      state.data.anonymousQuestions = state.data.anonymousQuestions.filter(
        (q) => q.id !== action.payload
      );
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.errorAll = action.payload;
      state.loadingAll = false;
      state.overviewLoading = false;
      state.monthlyLoading = false;
      state.weeklyLoading = false;
      state.dailyLoading = false;
      state.triggersLoading = false;
      state.contactsLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardDataAction.pending, (state) => {
        state.loadingAll = true;
        state.overviewLoading = true;
        state.errorAll = null;
      })
      .addCase(fetchDashboardDataAction.fulfilled, (state, action) => {
        state.loadingAll = false;
        state.overviewLoading = false;
        state.status = action.payload.status;
        state.data = {
          ...state.data,
          overview: {
            ...state.data.overview,
            all: action.payload.overview,
          },
          by_source: {
            ...state.data.by_source,
            all: action.payload.by_source,
          },
          by_triggers: {
            ...state.data.by_triggers,
            all: action.payload.by_triggers,
          },
          ratings: {
            ...state.data.ratings,
            all: action.payload.ratings,
          },
        };
        state.errorAll = null;
      })
      .addCase(fetchViewsTriggersDataAction.pending, (state) => {
        state.loadingAll = true;
        state.monthlyLoading = true;
        state.errorAll = null;
      })
      .addCase(fetchViewsTriggersDataAction.fulfilled, (state, action) => {
        state.loadingAll = false;
        state.monthlyLoading = false;

        // Limit daily views data to prevent memory issues (keep only last 30 days)
        const limitedViews =
          action.payload?.views && Array.isArray(action.payload.views)
            ? action.payload.views.slice(-30)
            : [];

        state.data = {
          ...state.data,
          overview: {
            ...state.data.overview,
            month: action.payload.summary,
          },
          by_triggers: {
            ...state.data.by_triggers,
            month: action.payload.summary.triggers || [],
          },
          views: {
            ...state.data.views,
            month: limitedViews,
          },
        };
        state.errorAll = null;
      })
      .addCase(fetchViewsTriggersDataAction.rejected, (state, action) => {
        state.loadingAll = false;
        state.monthlyLoading = false;
        state.errorAll = action.payload as string;
      })
      .addCase(fetchYearlyViewsTriggersDataAction.pending, (state) => {
        state.loadingAll = true;
        state.errorAll = null;
      })
      .addCase(
        fetchYearlyViewsTriggersDataAction.fulfilled,
        (state, action) => {
          state.loadingAll = false;

          // Limit yearly views data to prevent memory issues (keep only last 12 months)
          const limitedViews =
            action.payload?.views && Array.isArray(action.payload.views)
              ? action.payload.views.slice(-12)
              : [];

          state.data = {
            ...state.data,
            overview: {
              ...state.data.overview,
              year: action.payload.summary,
            },
            by_triggers: {
              ...state.data.by_triggers,
              year: action.payload.summary.triggers,
            },
            views: {
              ...state.data.views,
              year: limitedViews,
            },
          };
          state.errorAll = null;
        }
      )
      .addCase(fetchYearlyViewsTriggersDataAction.rejected, (state, action) => {
        state.loadingAll = false;
        state.errorAll = action.payload as string;
      })
      .addCase(fetchDashboardDataAction.rejected, (state, action) => {
        state.loadingAll = false;
        state.overviewLoading = false;
        state.errorAll = action.payload as string;
      })
      .addCase(fetchWeeklyViewsTriggersDataAction.pending, (state) => {
        state.loadingAll = true;
        state.weeklyLoading = true;
        state.errorAll = null;
      })
      .addCase(
        fetchWeeklyViewsTriggersDataAction.fulfilled,
        (state, action) => {
          state.loadingAll = false;
          state.weeklyLoading = false;

          // Limit weekly views data to prevent memory issues (keep only last 7 days)
          const limitedViews =
            action.payload?.views && Array.isArray(action.payload.views)
              ? action.payload.views.slice(-7)
              : [];

          state.data = {
            ...state.data,
            overview: {
              ...state.data.overview,
              week: action.payload.summary,
            },
            by_triggers: {
              ...state.data.by_triggers,
              week: action.payload.summary.triggers || [],
            },
            views: {
              ...state.data.views,
              week: limitedViews,
            },
          };
          state.errorAll = null;
        }
      )
      .addCase(fetchWeeklyViewsTriggersDataAction.rejected, (state, action) => {
        state.loadingAll = false;
        state.weeklyLoading = false;
        state.errorAll = action.payload as string;
      })
      .addCase(fetchDailyViewsTriggersDataAction.pending, (state) => {
        state.loadingAll = true;
        state.dailyLoading = true;
        state.errorAll = null;
      })
      .addCase(fetchDailyViewsTriggersDataAction.fulfilled, (state, action) => {
        state.loadingAll = false;
        state.dailyLoading = false;

        // Limit daily views data to prevent memory issues (keep only last 7 days)
        const limitedViews =
          action.payload?.views && Array.isArray(action.payload.views)
            ? action.payload.views.slice(-7)
            : [];

        state.data = {
          ...state.data,
          overview: {
            ...state.data.overview,
            day: action.payload.summary,
          },
          by_triggers: {
            ...state.data.by_triggers,
            day: action.payload.summary.triggers || [],
          },
          views: {
            ...state.data.views,
            day: limitedViews,
          },
        };
        state.errorAll = null;
      })
      .addCase(fetchDailyViewsTriggersDataAction.rejected, (state, action) => {
        state.loadingAll = false;
        state.dailyLoading = false;
        state.errorAll = action.payload as string;
      })
      .addCase(fetchForeverViewsTriggersDataAction.pending, (state) => {
        state.loadingAll = true;
        state.errorAll = null;
      })
      .addCase(
        fetchForeverViewsTriggersDataAction.fulfilled,
        (state, action) => {
          state.loadingAll = false;

          // Store forever data in the by_triggers.forever field
          state.data = {
            ...state.data,
            by_triggers: {
              ...state.data.by_triggers,
              forever: action.payload.summary.triggers || [],
            },
          };
          state.errorAll = null;
        }
      )
      .addCase(
        fetchForeverViewsTriggersDataAction.rejected,
        (state, action) => {
          state.loadingAll = false;
          state.errorAll = action.payload as string;
        }
      )
      .addCase(fetchTriggersDataAction.pending, (state) => {
        state.triggersLoading = true;
        state.errorAll = null;
      })
      .addCase(fetchTriggersDataAction.fulfilled, (state, action) => {
        state.triggersLoading = false;
        state.data.triggers = action.payload;
        state.errorAll = null;
      })
      .addCase(fetchTriggersDataAction.rejected, (state, action) => {
        state.triggersLoading = false;
        state.errorAll = action.payload as string;
      })
      .addCase(fetchDashboardDataWithPeriodAction.pending, (state) => {
        state.loadingAll = true;
        state.errorAll = null;
      })
      .addCase(
        fetchDashboardDataWithPeriodAction.fulfilled,
        (state, action) => {
          state.loadingAll = false;
          const { period, data } = action.payload;

          // Update the specific period data
          if (period === "day" || period === "today") {
            state.dailyLoading = false;
            state.data.by_source.day = data.by_source;
            state.data.ratings.day = data.ratings;
          } else if (period === "week") {
            state.weeklyLoading = false;
            state.data.by_source.week = data.by_source;
            state.data.ratings.week = data.ratings;
          }

          state.errorAll = null;
        }
      )
      .addCase(fetchDashboardDataWithPeriodAction.rejected, (state, action) => {
        state.loadingAll = false;
        state.errorAll = action.payload as string;
      })
      .addCase(fetchContactsDataAction.pending, (state) => {
        state.contactsLoading = true;
        state.errorAll = null;
      })
      .addCase(fetchContactsDataAction.fulfilled, (state, action) => {
        state.contactsLoading = false;
        const { contacts, pagination, append } = action.payload;

        if (append) {
          // Append new contacts to existing ones
          state.data.contacts = [...state.data.contacts, ...(contacts || [])];
        } else {
          // Replace contacts (new search/filter)
          state.data.contacts = contacts || [];
        }

        state.data.contactsPagination = pagination;
        state.errorAll = null;
      })
      .addCase(fetchContactsDataAction.rejected, (state, action) => {
        state.contactsLoading = false;
        state.errorAll = action.payload as string;
      })
      .addCase(createShortLinkAction.fulfilled, (state, action) => {
        const { triggerId, short_link_id } = action.payload;
        // Update the trigger in the triggers array
        const triggerIndex = state.data.triggers.findIndex(
          (trigger) => trigger.ID === triggerId
        );
        if (triggerIndex !== -1) {
          state.data.triggers[triggerIndex].short_link_id = short_link_id;
        }
      })
      .addCase(fetchStatusMeAction.pending, (state) => {
        state.loadingAll = true;
        state.errorAll = null;
      })
      .addCase(fetchStatusMeAction.fulfilled, (state, action) => {
        state.loadingAll = false;
        // Extract status from the API response structure
        state.status = action.payload.data?.status || action.payload as unknown as StatusInfo;
        state.errorAll = null;
      })
      .addCase(fetchStatusMeAction.rejected, (state, action) => {
        state.loadingAll = false;
        state.errorAll = action.payload as string;
      })
      .addCase(fetchViewsSourcesDataAction.pending, (state) => {
        state.loadingAll = true;
        state.errorAll = null;
      })
      .addCase(fetchViewsSourcesDataAction.fulfilled, (state, action) => {
        state.loadingAll = false;
        state.data.monthlySourcesData = action.payload;
        state.errorAll = null;
      })
      .addCase(fetchViewsSourcesDataAction.rejected, (state, action) => {
        state.loadingAll = false;
        state.errorAll = action.payload as string;
      })
      .addCase(fetchRatingsDataAction.pending, (state) => {
        state.ratingsLoading = true;
        state.errorAll = null;
      })
      .addCase(fetchRatingsDataAction.fulfilled, (state, action) => {
        state.ratingsLoading = false;
        state.data.ratings.filtered = action.payload;
        state.errorAll = null;
      })
      .addCase(fetchRatingsDataAction.rejected, (state, action) => {
        state.ratingsLoading = false;
        state.errorAll = action.payload as string;
      })
      .addCase(fetchAnonymousQuestionsAction.pending, (state) => {
        state.anonymousQuestionsLoading = true;
      })
      .addCase(fetchAnonymousQuestionsAction.fulfilled, (state, action) => {
        state.anonymousQuestionsLoading = false;
        state.data.anonymousQuestions = action.payload.map((box) => ({
          id: box.id,
          content: box.title,
          public_id: box.public_id,
          short_code: box.short_code,
          admin_id: box.admin_id,
          created_at: box.created_at,
          total_questions: box.total_questions,
        }));
      })
      .addCase(fetchAnonymousQuestionsAction.rejected, (state) => {
        state.anonymousQuestionsLoading = false;
      })
      .addCase(deleteAnonymousQuestionAction.fulfilled, (state, action) => {
        state.data.anonymousQuestions = state.data.anonymousQuestions.filter(
          (q) => q.id !== action.payload
        );
      });
  },
});

export const {
  addAnonymousQuestion,
  setAnonymousQuestions,
  deleteAnonymousQuestion,
  clearData,
  setError,
  setStatusIdFromUrl,
} =
  dashboardSlice.actions;
export default dashboardSlice.reducer;
